import React, { useState, useEffect } from 'react';
import doaService from '../../services/doaService';

const DoAFormModal = ({ isOpen, onClose, onSuccess, initialData = null }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    maxApprovalAmount: '',
    currency: 'USD',
    isActive: true
  });

  useEffect(() => {
    if (isOpen) {
      // Logic: Only allow editing if initialData is active
      if (initialData && initialData.isActive === false) {
        alert("Inactive rules cannot be edited. Please reactivate the rule first.");
        onClose();
        return;
      }

      loadRoles();
      
      if (initialData) {
        setFormData({
          role: initialData.role?._id || initialData.role,
          maxApprovalAmount: initialData.maxApprovalAmount,
          currency: initialData.currency || 'USD',
          isActive: initialData.isActive
        });
      } else {
        setFormData({ role: '', maxApprovalAmount: '', currency: 'USD', isActive: true });
      }
    }
  }, [isOpen, initialData]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const res = await doaService.getRoles();
      if (res && res.success && Array.isArray(res.data)) {
        setRoles(res.data);
      }
    } catch (err) {
      console.error("Failed to load roles:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Safety check again before submission
    if (initialData && initialData.isActive === false) {
        alert("Cannot save changes to an inactive rule.");
        return;
    }

    setSubmitting(true);
    try {
      let response;
      if (initialData?._id) {
        response = await doaService.updateDoAConfig(initialData._id, formData);
      } else {
        response = await doaService.createDoAConfig(formData);
      }

      if (response && response.success) {
        onSuccess(initialData ? 'Rule updated' : 'Rule created');
        onClose();
      } else {
        alert(response.message || "Operation failed");
      }
    } catch (err) {
      console.error("Submit error", err);
      alert(err.response?.data?.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '20px',
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '520px',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
          {initialData ? 'Edit Authority Rule' : 'Create Authority Rule'}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '12px' }}>
            
            <label style={{ display: 'grid', gap: '6px' }}>
              Organizational Role
              <select
                required
                disabled={initialData || loading}
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: initialData ? '#f9fafb' : '#ffffff'
                }}
              >
                <option value="">{loading ? 'Loading...' : 'Select a role...'}</option>
                {roles.map((r) => (
                  <option key={r._id || r.id} value={r._id || r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <label style={{ display: 'grid', gap: '6px' }}>
                Max Approval Amount
                <input
                  type="number"
                  required
                  min="0"
                  placeholder="0.00"
                  value={formData.maxApprovalAmount}
                  onChange={(e) => setFormData({ ...formData, maxApprovalAmount: e.target.value })}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                  }}
                />
              </label>

              <label style={{ display: 'grid', gap: '6px' }}>
                Currency
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </label>
            </div>

            {/* If it's an existing rule, we might want to hide or disable the toggle if it's inactive */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '8px' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '14px', color: '#374151' }}>Active Rule (Enforced in workflow)</span>
            </label>

          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'var(--primary-color, #2563eb)',
                color: '#ffffff',
                cursor: 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Saving...' : initialData ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoAFormModal;